import React from "react";
import { ChevronLeft, ChevronRight, Minimize2 } from "lucide-react";
import { EditCell } from "@/components/canvas/EditCell";
import { DropZone } from "@/components/canvas/kalkyle/DropZone";
import { Need, FormBlock, LinesArea, Classify } from "@/components/canvas/kalkyle/PostBlocks";
import { humanGroup } from "@/lib/kalkyle";
import { roleSuggestion } from "@/lib/krav";

const quiet = "inline-flex h-8 items-center gap-1.5 rounded-[8px] px-2.5 text-[13.5px] font-medium text-foreground/65 transition-colors hover:bg-black/[0.05] hover:text-foreground disabled:opacity-30";

/**
 * Focus – level three, on request: the whole surface for one post. Same blocks as the panel, more room, nothing extra.
 * «Tilbake til Skrog · Post 3 av 7» above, Forrige/Neste below. Reads like an editor page, not a form.
 */
export function Focus({ item, items, pos, ops, onBack, onPrev, onNext, onAddLine, onCatalog, onSource, onDetails, onDrop, justAdded, forslag, suggest, setSuggest }) {
  const others = items.filter((i) => i.id !== item.id);
  const role = roleSuggestion(item);
  const group = pos?.group ? humanGroup(pos.group) : null;
  return (
    <DropZone onDrop={(p) => onDrop(item, p)} className="mx-auto w-full max-w-[820px] px-5 pb-16 pt-3 sm:px-8" testId={`kalkyle-focus-${item.ref}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <button type="button" onClick={onBack} className="inline-flex h-8 items-center gap-1 rounded-[8px] px-2 text-[13.5px] font-medium text-foreground/60 transition-colors hover:bg-black/[0.05] hover:text-foreground" data-testid="focus-back"><ChevronLeft className="h-4 w-4" /> {group ? `Tilbake til ${group}` : "Alle poster"}</button>
          {pos ? <span className="text-[13px] text-muted-foreground" data-testid="focus-pos">Post {pos.index + 1} av {pos.total}</span> : null}
        </div>
        <div className="flex items-center gap-0.5">
          <button type="button" onClick={onDetails} className={quiet} data-testid="focus-details">Vis detaljer</button>
          <button type="button" onClick={onBack} className={quiet} title="Tilbake til panelet" data-testid="focus-collapse"><Minimize2 className="h-3.5 w-3.5" /> Panel</button>
        </div>
      </div>

      <header className="mt-5">
        <div className="flex items-center gap-1.5 text-[13px] text-muted-foreground"><span className="font-mono">{item.ref}</span>{item.sfi ? <><span className="text-muted-foreground/40">·</span><span>SFI {item.sfi}</span></> : null}{item.wo_no ? <><span className="text-muted-foreground/40">·</span><span>WO {item.wo_no}</span></> : null}</div>
        <h2 className="mt-0.5 text-[22px] font-semibold leading-[1.25] tracking-[-0.02em] text-foreground sm:text-[24px]"><EditCell value={item.title} onSave={(v) => ops.patchItem(item.id, "title", v)} testId={`focus-title-${item.ref}`} /></h2>
      </header>

      <div className="mt-6 space-y-7">
        <Need item={item} onSource={onSource} testId="focus" />
        {role ? <Classify item={item} role={role} ops={ops} testId="focus" /> : (
          <>
            <FormBlock item={item} others={others} ops={ops} testId="focus" />
            <LinesArea item={item} ops={ops} onAddLine={onAddLine} justAdded={justAdded} forslag={forslag} suggest={suggest} setSuggest={setSuggest} onCatalog={onCatalog} testId="focus" />
          </>
        )}
      </div>

      <nav className="mt-10 flex items-center justify-between border-t border-black/[0.06] pt-3 text-[13.5px]" aria-label="Mellom postene">
        <button type="button" onClick={onPrev} disabled={!onPrev} className={quiet} data-testid="focus-prev"><ChevronLeft className="h-4 w-4" /> Forrige post</button>
        <button type="button" onClick={onNext} disabled={!onNext} className={quiet} data-testid="focus-next">Neste post <ChevronRight className="h-4 w-4" /></button>
      </nav>
    </DropZone>
  );
}
